import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { conversationsKeys } from '@/features/conversations/keys';

/**
 * Game-accounts sidebar-block data hooks (Contract C
 * `GET /api/v1/conversations/{id}/game-accounts` +
 * `PATCH /api/v1/conversations/{id}/game-accounts/field`).
 *
 * Mirror of the Google Sheets customer game-accounts registry: the Hub
 * matches THIS conversation's contact phone against the mirrored sheet rows
 * and returns the customer's per-platform account ids as ORDERED
 * `[column_name, value]` pairs (sheet column order preserved; the PHONE
 * column and empty cells are excluded server-side), PLUS the full ordered
 * data-column list of the customer's source tab (`columns` — PHONE excluded,
 * MEMBER included, `null` when `found=false`) so the sidebar can render
 * fillable EMPTY rows for columns the customer has no value in yet.
 * `found=false` is a NORMAL state — the contact has no phone or no mirror
 * row — not an error; only a nonexistent conversation 404s. The two flavors
 * are NOT interchangeable in the UI: no phone (a group chat) has nothing to
 * offer, while a phone with no registry row is a NEW CUSTOMER the agent can
 * register in one click (`useRegisterMember`).
 *
 * DUPLICATE PHONES are a hard data error, not a row: a phone appearing more
 * than once in the registry's PHONE column is excluded from the mirror
 * entirely, so the Hub answers `duplicate=true` + `duplicate_members` (the
 * MEMBER value of every offending row) with `found=false` / `fields=[]` /
 * `columns=null` — the sidebar shows a "clean the sheet first" warning
 * instead of accounts, and the field PATCH 409s (no unique row to write to).
 *
 * Cells are EDITABLE (same posture as the 备注 field — every agent may
 * write): `useUpdateGameAccountField` PATCHes one cell straight through to
 * the spreadsheet; sheet-side edits keep flowing back via the 5-minute sync.
 * Last write wins on races.
 */

// --- Schema (mirror the fixed game-accounts contract) ----------------------

/**
 * The two duplicate keys carry `.default(...)` rather than being required:
 * the Hub always sends them, but frontend and hub are separate containers, so
 * a bundle that lands before the hub restart would otherwise fail the whole
 * parse (this schema is strict — a missing key blanks the section behind
 * 无法加载游戏账号). The defaults are the contract's own "normal phone" values,
 * so the accepted value domain is unchanged.
 */
export const gameAccountsSchema = z.object({
  found: z.boolean(),
  phone_e164: z.string().nullable(),
  // Ordered [[column_name, value], ...] preserving sheet column order.
  fields: z.array(z.tuple([z.string(), z.string()])),
  // Full ordered data-column list of the customer's source tab (PHONE
  // excluded server-side, MEMBER included); null when found=false.
  columns: z.array(z.string()).nullable(),
  // ISO datetime string of the last successful sheet sync. Still meaningful
  // in the duplicate case (it is the whole mirror's freshness stamp).
  synced_at: z.string().nullable(),
  source_tab: z.string().nullable(),
  // The contact's phone occurs MORE THAN ONCE in the registry's PHONE column
  // — a data error, so the mirror carries no row for it at all (found=false,
  // fields=[], columns=null) and the sidebar renders a cleanup warning.
  duplicate: z.boolean().default(false),
  // MEMBER values of every duplicate row (first-seen order, non-empty only,
  // de-duplicated — may be an EMPTY array when no occurrence has a MEMBER);
  // null on a normal phone.
  duplicate_members: z.array(z.string()).nullable().default(null),
});

export type GameAccounts = z.infer<typeof gameAccountsSchema>;

/**
 * Fetch the game-accounts mirror row for a single conversation.
 *
 * Keyed under the conversations root (`['conversations', 'game-accounts',
 * id]`, see `keys.ts`) alongside the `profile` slot the sidebar already
 * uses. `staleTime` 60s: the mirror only changes on a background sheet sync
 * (or a console edit, which reconciles the cache itself via
 * `useUpdateGameAccountField`), so re-opening the sidebar within a minute
 * serves the cached row.
 */
export function useGameAccounts(conversationId: string) {
  return useQuery<GameAccounts, Error>({
    queryKey: conversationsKeys.gameAccounts(conversationId),
    queryFn: async () => {
      const response = await apiClient.get(
        `/conversations/${conversationId}/game-accounts`,
      );
      return gameAccountsSchema.parse(response.data);
    },
    staleTime: 60_000,
  });
}

// --- Field edit mutation ---------------------------------------------------

interface UpdateGameAccountFieldVariables {
  conversationId: string;
  /** Sheet column to write — must be one of the customer's tab `columns`. */
  column: string;
  /** New cell value; `''` clears the cell. */
  value: string;
}

/**
 * Human-readable save-failure message: the Hub answers with a specific
 * `detail` string (409 when the customer has no registry row / their phone is
 * a DUPLICATE so no unique row can be located / the column is unknown, 502
 * when the Sheets write itself failed) — surface it verbatim so
 * the agent knows WHY the cell did not change; anything without one (network
 * blip, non-axios throw) falls back to the generic 保存失败.
 */
function serverDetail(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return detail;
    }
  }
  return fallback;
}

function saveErrorMessage(error: unknown): string {
  return serverDetail(error, '保存失败');
}

/**
 * Write ONE game-account cell back to the spreadsheet
 * (`PATCH /conversations/{id}/game-accounts/field`, body
 * `{ column, value }` — `value: ""` clears the cell).
 *
 * The Hub writes the sheet cell first (RAW), then updates its mirror row and
 * returns the fresh `GameAccountsOut` — so on success the response IS server
 * truth and is written straight into this conversation's `game-accounts`
 * cache slot (no optimistic patch, no refetch needed: the sidebar re-renders
 * from the reconciled cache immediately). Every OTHER conversation's
 * game-accounts slot is then invalidated: in a merged thread the sibling
 * member conversations mirror the SAME sheet row, so their cached copies
 * just went stale (prefix invalidation via the shared key factory; inactive
 * slots simply refetch on next mount). On failure the cache was never
 * touched, so the row keeps showing the previous value; the hook toasts the
 * server's `detail` when present, else `保存失败`.
 */
export function useUpdateGameAccountField() {
  const queryClient = useQueryClient();

  return useMutation<GameAccounts, unknown, UpdateGameAccountFieldVariables>({
    mutationFn: async ({ conversationId, column, value }) => {
      const response = await apiClient.patch(
        `/conversations/${conversationId}/game-accounts/field`,
        { column, value },
      );
      return gameAccountsSchema.parse(response.data);
    },

    onSuccess: (data, { conversationId }) => {
      queryClient.setQueryData(
        conversationsKeys.gameAccounts(conversationId),
        data,
      );
      // Merged-thread siblings mirror the SAME sheet row — mark every other
      // game-accounts slot stale (the edited slot was reconciled above, so
      // exclude it from the refetch).
      void queryClient.invalidateQueries({
        queryKey: [...conversationsKeys.all, 'game-accounts'],
        predicate: (query) => query.queryKey[2] !== conversationId,
      });
      toast('已保存并同步表格');
    },

    onError: (error) => {
      toast(saveErrorMessage(error));
    },
  });
}

// --- Registration mutation -------------------------------------------------

/**
 * Register a NEW customer into the Sheets registry
 * (`POST /conversations/{id}/game-accounts`, no body).
 *
 * A customer whose number is not in the registry answers `found=false` — the
 * sidebar's 新顾客 state. The registry pre-creates rows carrying only their
 * MEMBER id, so registering is ONE cell write: the Hub types the customer's
 * phone into the reserved row below the last registered member, and the
 * sheet's PHONE formula derives the key column from it. The Hub then mirrors
 * the harvested row and returns the SAME `GameAccountsOut` shape the GET
 * serves, so the response is written straight into this conversation's cache
 * slot and the sidebar flips from 新顾客 to a full member profile — member id
 * included — without waiting out the 5-minute sync interval.
 *
 * Merged-thread siblings key on the same phone, so their slots are
 * invalidated exactly as the cell edit does. On failure the cache is never
 * touched (the customer stays 新顾客) and the server's `detail` is toasted
 * verbatim: it names the actual obstacle — already a member, the phone
 * duplicated in the registry, the reserved rows exhausted, or another agent
 * winning the slot race — each of which needs a different response from the
 * agent.
 */
export function useRegisterMember() {
  const queryClient = useQueryClient();

  return useMutation<GameAccounts, unknown, { conversationId: string }>({
    mutationFn: async ({ conversationId }) => {
      const response = await apiClient.post(
        `/conversations/${conversationId}/game-accounts`,
      );
      return gameAccountsSchema.parse(response.data);
    },

    onSuccess: (data, { conversationId }) => {
      queryClient.setQueryData(
        conversationsKeys.gameAccounts(conversationId),
        data,
      );
      void queryClient.invalidateQueries({
        queryKey: [...conversationsKeys.all, 'game-accounts'],
        predicate: (query) => query.queryKey[2] !== conversationId,
      });
      toast('已注册会员并写入表格');
    },

    onError: (error) => {
      toast(serverDetail(error, '注册失败'));
    },
  });
}
